if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInputs.forEach((input) => {
          input.disabled = false;
        });
        this.id = this.form.querySelector('[name="id"]')?.value;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.sellingPlanSelect = this.form.querySelector('[id*="ProductSellingPlan-"]');
        this.sellingPlanInput = this.form.querySelector('[name="selling_plan"]');
        this.quantityInput = this.form.querySelector('[name="quantity"]');
        this.giftWithPurchase = this.form.querySelector('[name="properties[_gifts_with_purchase]"]')?.value;
        this.acceleratedCheckout = this.querySelector('[data-accelerated-checkout');
        this.giftAdded = false;

        if (this.acceleratedCheckout) {
          this.acceleratedCheckout.addEventListener('click', this.onSubmitAcceleratedCheckout.bind(this));
        }

        if (this.sellingPlanSelect)
          this.sellingPlanSelect.addEventListener('change', this.onSellingPlanChange.bind(this));

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      cartUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'cart-remove' && event.remove === this.id) {
            this.giftAdded = false;
          }
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      onSellingPlanChange(evt) {
        const selectedOption = evt.target.selectedOptions[0];
        const id = selectedOption.getAttribute('data-selling-plan-id');
        if (this.sellingPlanInput) this.sellingPlanInput.value = id;
      }

      getRequestConfig(formData) {
        const sections = this.cart.getSectionsToRender().map((section) => section.id);
        const removeKey = this.sellingPlanInput?.value
          ? `selling_plan=${this.sellingPlanInput.value}&variant=${formData.get('id')}`
          : `variant=${formData.get('id')}`;

        const buildGiftItems = () => {
          if (!this.giftWithPurchase || this.giftAdded) return [];
          return this.giftWithPurchase
            .split(':')[1]
            .split(',')
            .map((giftId) => ({
              id: giftId,
              quantity: 1,
              properties: {
                _gift_id: this.giftWithPurchase,
                _gift_remove_key: removeKey,
              },
            }));
        };

        const buildProperties = () => {
          return Object.fromEntries(
            [...formData.entries()]
              .filter(([name]) => name.startsWith('properties[') && !name.includes('_gifts_with_purchase'))
              .map(([name, value]) => [name.replace('properties[', '').replace(']', ''), value])
          );
        };

        if (this.giftWithPurchase) {
          const giftItems = buildGiftItems();
          const properties = buildProperties();

          const items = [
            ...giftItems,
            {
              id: formData.get('id'),
              quantity: this.quantityInput?.value,
              ...(this.sellingPlanInput?.value && { selling_plan: this.sellingPlanInput.value }),
              properties,
            },
          ];

          return {
            ...fetchConfig(),
            body: JSON.stringify({
              items,
              sections,
              sections_url: window.location.pathname,
            }),
          };
        }

        // Default case when no gift with purchase
        if (this.cart) {
          formData.append('sections', sections);
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        return {
          ...fetchConfig('javascript'),
          body: formData,
        };
      }

      onSubmitAcceleratedCheckout(evt) {
        evt.preventDefault();
        evt.target.disabled = true;
        evt.target.classList.add('loading');

        const formData = new FormData(this.form);
        const config = this.getRequestConfig(formData);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            window.location.href = '/checkout?payment=shop_pay';
          });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        const formData = new FormData(this.form);
        const config = this.getRequestConfig(formData);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.giftWithPurchase && !this.giftAdded) {
              this.giftAdded = true;
            }
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInputs() {
        return this.form.querySelectorAll('[name^=id]');
      }
    }
  );
}
